import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
    Plus, 
    Receipt,
    Calendar,
    Tag,
    Trash2,
    Edit,
    CheckCircle,
    Clock,
    AlertCircle,
    Loader2,
    MoreVertical,
    DollarSign,
    Filter,
    Settings,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExpensesPage = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    
    const [expenses, setExpenses] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    // Filters
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterTag, setFilterTag] = useState('all');
    const [filterType, setFilterType] = useState('all');
    
    // Dialogs
    const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    
    // Form state
    const [editingExpense, setEditingExpense] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        amount: '',
        tag_id: '',
        due_date: '',
        expense_type: 'eventual',
        recurrence_day: '',
        notes: '',
        is_asset: false  // Ativo/Revenda - não conta no lucro
    });
    
    // Tag form
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('#6b7280');
    
    const TOKEN = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${TOKEN}` } };

    useEffect(() => {
        fetchExpenses();
        fetchTags();
    }, []);

    const fetchExpenses = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/expenses`, config);
            setExpenses(response.data);
        } catch (error) {
            toast.error('Erro ao carregar despesas');
        } finally {
            setLoading(false);
        }
    };

    const fetchTags = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/expense-tags`, config);
            setTags(response.data);
        } catch (error) {
            console.error('Error fetching tags:', error);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR');
    };

    const resetForm = () => {
        setFormData({
            title: '',
            amount: '',
            tag_id: '',
            due_date: '',
            expense_type: 'eventual',
            recurrence_day: '',
            notes: '',
            is_asset: false
        });
        setEditingExpense(null);
    };

    const openCreateDialog = () => {
        resetForm();
        // Set default due date to today
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({ ...prev, due_date: today }));
        setExpenseDialogOpen(true);
    };

    const openEditDialog = (expense) => {
        setEditingExpense(expense);
        setFormData({
            title: expense.title,
            amount: expense.amount.toString(),
            tag_id: expense.tag_id || '',
            due_date: expense.due_date.split('T')[0],
            expense_type: expense.expense_type,
            recurrence_day: expense.recurrence_day?.toString() || '',
            notes: expense.notes || '',
            is_asset: expense.is_asset || false
        });
        setExpenseDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.amount || !formData.due_date) {
            toast.error('Preencha título, valor e vencimento');
            return;
        }
        
        // Validate recurrence_day for fixed expenses
        if (formData.expense_type === 'fixa') {
            const day = parseInt(formData.recurrence_day);
            if (!day || day < 1 || day > 31) {
                toast.error('Para despesa fixa, informe o dia de recorrência (1-31)');
                return;
            }
        }

        setProcessing(true);
        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                tag_id: formData.tag_id || null,
                recurrence_day: formData.expense_type === 'fixa' ? parseInt(formData.recurrence_day) : null
            };

            if (editingExpense) {
                await axios.put(`${API_URL}/api/expenses/${editingExpense.id}`, payload, config);
                toast.success('Despesa atualizada!');
            } else {
                await axios.post(`${API_URL}/api/expenses`, payload, config);
                toast.success('Despesa criada!');
            }
            
            setExpenseDialogOpen(false);
            resetForm();
            fetchExpenses();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar despesa');
        } finally {
            setProcessing(false);
        }
    };

    const handlePayExpense = async () => {
        if (!selectedExpense) return;
        
        setProcessing(true);
        try {
            await axios.put(`${API_URL}/api/expenses/${selectedExpense.id}/pay`, {}, config);
            toast.success('Despesa marcada como paga! Saída registrada no caixa.');
            setPayDialogOpen(false);
            setSelectedExpense(null);
            fetchExpenses();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao pagar despesa');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteExpense = async () => {
        if (!selectedExpense) return;
        
        setProcessing(true);
        try {
            await axios.delete(`${API_URL}/api/expenses/${selectedExpense.id}`, config);
            toast.success('Despesa excluída!');
            setDeleteDialogOpen(false);
            setSelectedExpense(null);
            fetchExpenses();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir despesa');
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateTag = async () => {
        if (!newTagName.trim()) {
            toast.error('Informe o nome da tag');
            return;
        }

        setProcessing(true);
        try {
            await axios.post(`${API_URL}/api/expense-tags`, {
                name: newTagName,
                color: newTagColor
            }, config);
            toast.success('Tag criada!');
            setNewTagName('');
            setNewTagColor('#6b7280');
            fetchTags();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar tag');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteTag = async (tagId) => {
        try {
            await axios.delete(`${API_URL}/api/expense-tags/${tagId}`, config);
            toast.success('Tag excluída!');
            fetchTags();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir tag');
        }
    };

    // Filter expenses
    const filteredExpenses = expenses.filter(expense => {
        if (filterStatus !== 'all' && expense.status !== filterStatus) return false;
        if (filterTag !== 'all' && expense.tag_id !== filterTag) return false;
        if (filterType !== 'all' && expense.expense_type !== filterType) return false;
        return true;
    });

    // Calculate totals
    const totalPendente = expenses.filter(e => e.status === 'pendente').reduce((sum, e) => sum + e.amount, 0);
    const totalPago = expenses.filter(e => e.status === 'paga').reduce((sum, e) => sum + e.amount, 0);

    // Check if expense is overdue
    const isOverdue = (expense) => {
        if (expense.status === 'paga') return false;
        const dueDate = new Date(expense.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dueDate < today;
    };

    const TAG_COLORS = [
        '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
        '#22c55e', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
        '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
        '#6b7280'
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold font-heading text-white flex items-center gap-2">
                        <Receipt className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
                        Despesas
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">Gerencie suas despesas fixas e eventuais</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    {isAdmin && (
                        <Button 
                            variant="outline" 
                            onClick={() => setTagDialogOpen(true)}
                            className="border-[#262626] text-gray-400 flex-1 sm:flex-none"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Tags
                        </Button>
                    )}
                    <Button 
                        onClick={openCreateDialog}
                        className="bg-red-500 text-black hover:bg-red-400 font-bold flex-1 sm:flex-none"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nova Despesa
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <Card className="bg-[#0a0a0a] border-[#262626]">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-500/10 rounded-lg">
                                <Clock className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wider">Pendentes</p>
                                <p className="text-lg sm:text-xl font-bold text-amber-500">{formatCurrency(totalPendente)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-[#0a0a0a] border-[#262626]">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-500/10 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase tracking-wider">Pagas</p>
                                <p className="text-lg sm:text-xl font-bold text-emerald-500">{formatCurrency(totalPago)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card className="bg-[#0a0a0a] border-[#262626]">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-400">Filtros</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="bg-[#121212] border-[#262626] text-white">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#262626]">
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="pendente">Pendentes</SelectItem>
                                <SelectItem value="paga">Pagas</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className="bg-[#121212] border-[#262626] text-white">
                                <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#262626]">
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="fixa">Fixa</SelectItem>
                                <SelectItem value="eventual">Eventual</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterTag} onValueChange={setFilterTag}>
                            <SelectTrigger className="bg-[#121212] border-[#262626] text-white">
                                <SelectValue placeholder="Tag" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#262626]">
                                <SelectItem value="all">Todas</SelectItem>
                                {tags.map(tag => (
                                    <SelectItem key={tag.id} value={tag.id}>
                                        <span className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }}></span>
                                            {tag.name}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Expenses List */}
            <Card className="bg-[#0a0a0a] border-[#262626]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white text-lg">
                        Despesas ({filteredExpenses.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {filteredExpenses.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Nenhuma despesa encontrada</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredExpenses.map(expense => (
                                <div 
                                    key={expense.id}
                                    className={`p-4 bg-[#121212] rounded-lg border ${
                                        isOverdue(expense) ? 'border-red-500/50' : 'border-[#262626]'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                                <h3 className="text-white font-semibold truncate">{expense.title}</h3>
                                                <Badge 
                                                    className={expense.status === 'paga' 
                                                        ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' 
                                                        : 'bg-amber-500/20 text-amber-500 border-amber-500/30'
                                                    }
                                                >
                                                    {expense.status === 'paga' ? 'Paga' : 'Pendente'}
                                                </Badge>
                                                <Badge variant="outline" className="border-[#333] text-gray-400 text-xs">
                                                    {expense.expense_type === 'fixa' ? 'Fixa' : 'Eventual'}
                                                </Badge>
                                                {expense.is_asset && (
                                                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                                                        Ativo/Revenda
                                                    </Badge>
                                                )}
                                                {isOverdue(expense) && (
                                                    <Badge className="bg-red-500/20 text-red-500 border-red-500/30 text-xs">
                                                        <AlertCircle className="w-3 h-3 mr-1" />
                                                        Vencida
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    Venc: {formatDate(expense.due_date)}
                                                </span>
                                                {expense.tag_name && (
                                                    <span className="flex items-center gap-1">
                                                        <span 
                                                            className="w-2 h-2 rounded-full" 
                                                            style={{ backgroundColor: expense.tag_color }}
                                                        ></span>
                                                        {expense.tag_name}
                                                    </span>
                                                )}
                                            </div>
                                            {expense.notes && (
                                                <p className="text-xs text-gray-500 mt-1 truncate">{expense.notes}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-red-500">{formatCurrency(expense.amount)}</p>
                                                {expense.paid_at && (
                                                    <p className="text-xs text-gray-500">Pago em {formatDate(expense.paid_at)}</p>
                                                )}
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="text-gray-400">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="bg-[#121212] border-[#262626]">
                                                    {expense.status === 'pendente' && (
                                                        <>
                                                            <DropdownMenuItem 
                                                                onClick={() => {
                                                                    setSelectedExpense(expense);
                                                                    setPayDialogOpen(true);
                                                                }}
                                                                className="text-emerald-500 focus:text-emerald-500"
                                                            >
                                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                                Marcar como Paga
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem 
                                                                onClick={() => openEditDialog(expense)}
                                                                className="text-white"
                                                            >
                                                                <Edit className="w-4 h-4 mr-2" />
                                                                Editar
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem 
                                                                onClick={() => {
                                                                    setSelectedExpense(expense);
                                                                    setDeleteDialogOpen(true);
                                                                }}
                                                                className="text-red-500 focus:text-red-500"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Excluir
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                    {expense.status === 'paga' && (
                                                        <DropdownMenuItem disabled className="text-gray-500">
                                                            Despesa já paga
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Expense Dialog */}
            <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-red-500" />
                            {editingExpense ? 'Editar Despesa' : 'Nova Despesa'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-gray-400">Título *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Ex: Aluguel, Conta de luz..."
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400">Valor *</Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.amount}
                                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                        className="bg-[#121212] border-[#262626] text-white pl-10"
                                        placeholder="0,00"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400">Vencimento *</Label>
                                <Input
                                    type="date"
                                    value={formData.due_date}
                                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400">Tipo</Label>
                                <Select 
                                    value={formData.expense_type} 
                                    onValueChange={(value) => setFormData({ ...formData, expense_type: value, recurrence_day: '' })}
                                >
                                    <SelectTrigger className="bg-[#121212] border-[#262626] text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-[#262626]">
                                        <SelectItem value="eventual">Eventual</SelectItem>
                                        <SelectItem value="fixa">Fixa (Recorrente)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400">Tag/Categoria</Label>
                                <Select 
                                    value={formData.tag_id || "none"} 
                                    onValueChange={(value) => setFormData({ ...formData, tag_id: value === "none" ? "" : value })}
                                >
                                    <SelectTrigger className="bg-[#121212] border-[#262626] text-white">
                                        <SelectValue placeholder="Selecione..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121212] border-[#262626]">
                                        <SelectItem value="none">Sem tag</SelectItem>
                                        {tags.map(tag => (
                                            <SelectItem key={tag.id} value={tag.id}>
                                                <span className="flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }}></span>
                                                    {tag.name}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {/* Recurrence Day - Only for Fixed expenses */}
                        {formData.expense_type === 'fixa' && (
                            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-red-500" />
                                    <Label className="text-red-500 font-medium">Dia de Recorrência</Label>
                                </div>
                                <p className="text-xs text-gray-400">
                                    Informe o dia do mês em que essa despesa se repete (ex: aluguel dia 5, internet dia 10)
                                </p>
                                <div className="flex items-center gap-3">
                                    <span className="text-gray-400">Todo dia</span>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={formData.recurrence_day}
                                        onChange={(e) => setFormData({ ...formData, recurrence_day: e.target.value })}
                                        className="bg-[#121212] border-[#262626] text-white w-20 text-center"
                                        placeholder="1-31"
                                    />
                                    <span className="text-gray-400">de cada mês</span>
                                </div>
                                <p className="text-xs text-emerald-400">
                                    ✓ A despesa será gerada automaticamente todo mês
                                </p>
                            </div>
                        )}
                        
                        {/* Checkbox Ativo/Revenda */}
                        <div className="p-4 bg-[#121212] rounded-lg border border-[#262626] space-y-3">
                            <div className="flex items-start gap-3">
                                <input
                                    type="checkbox"
                                    id="is_asset"
                                    checked={formData.is_asset}
                                    onChange={(e) => setFormData({ ...formData, is_asset: e.target.checked })}
                                    className="w-5 h-5 mt-0.5 rounded border-[#262626] bg-[#0a0a0a] text-red-500 focus:ring-red-500"
                                />
                                <div>
                                    <Label htmlFor="is_asset" className="text-white font-medium cursor-pointer">
                                        Ativo da Empresa / Revenda
                                    </Label>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Marque se esta despesa é um produto para revenda ou ativo da empresa. 
                                        <span className="text-amber-500"> Não será contabilizada no lucro.</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Observações</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Anotações opcionais..."
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setExpenseDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            disabled={processing}
                            className="bg-red-500 text-black hover:bg-red-400 font-bold"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingExpense ? 'Salvar' : 'Criar')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Tags Management Dialog (Admin Only) */}
            <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Tag className="w-5 h-5 text-red-500" />
                            Gerenciar Tags
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Crie e gerencie as tags de categorias de despesas
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Create new tag */}
                        <div className="p-4 bg-[#121212] rounded-lg border border-[#262626]">
                            <p className="text-sm text-gray-400 mb-3">Nova Tag</p>
                            <div className="flex gap-2">
                                <Input
                                    value={newTagName}
                                    onChange={(e) => setNewTagName(e.target.value)}
                                    className="bg-[#0a0a0a] border-[#262626] text-white flex-1"
                                    placeholder="Nome da tag..."
                                />
                                <div className="relative">
                                    <input
                                        type="color"
                                        value={newTagColor}
                                        onChange={(e) => setNewTagColor(e.target.value)}
                                        className="w-10 h-10 rounded cursor-pointer border border-[#262626]"
                                    />
                                </div>
                                <Button 
                                    onClick={handleCreateTag}
                                    disabled={processing}
                                    className="bg-red-500 text-black hover:bg-red-400"
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        
                        {/* Existing tags */}
                        <div className="space-y-2">
                            <p className="text-sm text-gray-400">Tags existentes</p>
                            {tags.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">Nenhuma tag criada</p>
                            ) : (
                                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                    {tags.map(tag => (
                                        <div 
                                            key={tag.id} 
                                            className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-[#262626]"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span 
                                                    className="w-4 h-4 rounded-full" 
                                                    style={{ backgroundColor: tag.color }}
                                                ></span>
                                                <span className="text-white">{tag.name}</span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDeleteTag(tag.id)}
                                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            onClick={() => setTagDialogOpen(false)}
                            className="bg-red-500 text-black hover:bg-red-400 font-bold"
                        >
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Pay Expense Confirmation Dialog */}
            <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                            Confirmar Pagamento
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedExpense && (
                        <div className="space-y-4 py-4">
                            <div className="p-4 bg-[#121212] rounded-lg border border-[#262626]">
                                <p className="text-white font-semibold">{selectedExpense.title}</p>
                                <p className="text-red-500 text-xl font-bold mt-2">{formatCurrency(selectedExpense.amount)}</p>
                            </div>
                            
                            <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                                <p className="text-amber-500 text-sm">
                                    <strong>Atenção:</strong> Ao marcar como paga, uma SAÍDA será registrada automaticamente no caixa atual.
                                </p>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setPayDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handlePayExpense}
                            disabled={processing}
                            className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Pagamento'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Expense Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-red-500" />
                            Excluir Despesa
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedExpense && (
                        <div className="py-4">
                            <p className="text-gray-400">
                                Tem certeza que deseja excluir a despesa <strong className="text-white">"{selectedExpense.title}"</strong>?
                            </p>
                            <p className="text-red-500 text-sm mt-2">Esta ação não pode ser desfeita.</p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setDeleteDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleDeleteExpense}
                            disabled={processing}
                            className="bg-red-500 hover:bg-red-400 text-white font-bold"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Excluir'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ExpensesPage;
